import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaPrint, FaSave, FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import { api } from '../api';

/* ── Helpers ── */
function defaultItem() {
  return { cantidad: '', unidad: 'PIEZA', atributo: '', clave: '', producto: '', precioUnit: '', fechaEntrega: '' };
}

const DEFAULT_ORDEN = {
  folio: '',
  fecha: new Date().toISOString().slice(0, 10),
  tc: '',
  sucursal: '',
  almacen: 'GENERAL',
  provNombre: '',
  provContacto: '',
  provTel: '',
  provFax: '',
  provRfc: '',
  provDir1: '',
  provDir2: '',
  provCiudad: '',
  provPais: 'MEXICO',
  entregarA: '',
  compradorNum: '',
  compradorNombre: '',
  items: [defaultItem()],
  ivaPorc: 16,
};

/* Convierte número a letras en español */
function numeroALetras(monto) {
  if (!monto || isNaN(+monto)) return '';
  const n      = parseFloat(monto);
  const entero = Math.floor(Math.abs(n));
  const cents  = Math.round((Math.abs(n) - entero) * 100);

  const u   = ['','UNO','DOS','TRES','CUATRO','CINCO','SEIS','SIETE','OCHO','NUEVE'];
  const s10 = ['DIEZ','ONCE','DOCE','TRECE','CATORCE','QUINCE','DIECISÉIS','DIECISIETE','DIECIOCHO','DIECINUEVE'];
  const v   = ['VEINTE','VEINTIUNO','VEINTIDÓS','VEINTITRÉS','VEINTICUATRO','VEINTICINCO','VEINTISÉIS','VEINTISIETE','VEINTIOCHO','VEINTINUEVE'];
  const d   = ['','','VEINTE','TREINTA','CUARENTA','CINCUENTA','SESENTA','SETENTA','OCHENTA','NOVENTA'];
  const h   = ['','CIENTO','DOSCIENTOS','TRESCIENTOS','CUATROCIENTOS','QUINIENTOS','SEISCIENTOS','SETECIENTOS','OCHOCIENTOS','NOVECIENTOS'];

  const dec = x => {
    if (!x) return '';
    if (x < 10)  return u[x];
    if (x < 20)  return s10[x - 10];
    if (x < 30)  return v[x - 20];
    return d[Math.floor(x / 10)] + (x % 10 ? ' Y ' + u[x % 10] : '');
  };
  const cen = x => {
    if (!x) return '';
    if (x === 100) return 'CIEN';
    const c = Math.floor(x / 100), r = x % 100;
    return (c ? h[c] : '') + (c && r ? ' ' : '') + dec(r);
  };
  const miles = x => {
    const m = Math.floor(x / 1000), r = x % 1000;
    const pre = m === 1 ? 'MIL' : cen(m) + ' MIL';
    return r ? pre + ' ' + cen(r) : pre;
  };

  let res;
  if (!entero)          res = 'CERO';
  else if (entero < 1000)    res = cen(entero);
  else if (entero < 1000000) res = miles(entero);
  else {
    const m = Math.floor(entero / 1000000), r = entero % 1000000;
    const pre = m === 1 ? 'UN MILLÓN' : cen(m) + ' MILLONES';
    res = r ? pre + ' ' + miles(r) : pre;
  }
  return `${res} PESOS ${String(cents).padStart(2, '0')}/100 M.N.`;
}

function fmt(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtFecha(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/* ── Componente principal ── */
export default function OrdenCompra() {
  const navigate       = useNavigate();
  const { id }         = useParams();
  const [searchParams] = useSearchParams();

  const [form, setForm]               = useState(DEFAULT_ORDEN);
  const [ordenId, setOrdenId]         = useState(null);
  const [saving, setSaving]           = useState(false);
  const [printTrigger, setPrintTrigger] = useState(0);

  useEffect(() => {
    if (!id) return;
    api.getOrden(id).then(data => {
      if (!data) return;
      setOrdenId(data.id);
      setForm(f => ({ ...f, ...data }));
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (searchParams.get('print') === '1' && ordenId) {
      setTimeout(() => setPrintTrigger(n => n + 1), 600);
    }
  }, [ordenId]); // eslint-disable-line

  useEffect(() => {
    if (printTrigger > 0) window.print();
  }, [printTrigger]);

  const set    = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setItem = (i, field, val) => setForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [field]: val };
    return { ...f, items };
  });
  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, defaultItem()] }));
  const removeItem = i  => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  /* Cálculos */
  const importe  = form.items.reduce((s, it) =>
    s + (parseFloat(it.cantidad) || 0) * (parseFloat(it.precioUnit) || 0), 0);
  const ivaAmt   = importe * ((parseFloat(form.ivaPorc) || 0) / 100);
  const total    = importe + ivaAmt;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const newId = ordenId || `orden_${Date.now()}`;
    try {
      await api.saveOrden({ id: newId, ...form });
      navigate('/fichas/ordenes');
    } catch (err) {
      alert('Error al guardar: ' + err.message);
      setSaving(false);
    }
  };

  const handlePrint = () => setPrintTrigger(n => n + 1);
  const handleReset = () => { setOrdenId(null); setForm(DEFAULT_ORDEN); };

  return (
    <>
      <PageStyle />
      <Page>

        {/* ── Encabezado ── */}
        <Header className="no-print">
          <Logo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
          <HeaderTitle>Orden de Compra</HeaderTitle>
          <HeaderActions>
            <ActionBtn onClick={() => navigate('/fichas/ordenes')}>
              <FaArrowLeft size={13} /> Órdenes
            </ActionBtn>
            <ActionBtn onClick={handleReset}>Limpiar</ActionBtn>
            <ActionBtn $save onClick={handleSave} disabled={saving}>
              <FaSave size={13} /> {saving ? 'Guardando…' : 'Guardar'}
            </ActionBtn>
            <ActionBtn $primary onClick={handlePrint}>
              <FaPrint size={14} /> Imprimir / PDF
            </ActionBtn>
          </HeaderActions>
        </Header>

        {/* ── Formulario ── */}
        <FormWrap className="no-print">

          {/* Datos de la orden */}
          <Card>
            <CardTitle>Datos de la orden</CardTitle>
            <FormGrid>
              <Field>
                <Label>Folio</Label>
                <Input value={form.folio} onChange={e => set('folio', e.target.value)} placeholder="Ej: OC-001" />
              </Field>
              <Field>
                <Label>Fecha</Label>
                <Input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} />
              </Field>
              <Field>
                <Label>T.C. (tipo de cambio)</Label>
                <Input value={form.tc} onChange={e => set('tc', e.target.value)} placeholder="Ej: 17.6690" />
              </Field>
              <Field>
                <Label>Sucursal</Label>
                <Input value={form.sucursal} onChange={e => set('sucursal', e.target.value)} placeholder="Ej: MAT" />
              </Field>
              <Field>
                <Label>Almacén</Label>
                <Input value={form.almacen} onChange={e => set('almacen', e.target.value)} placeholder="GENERAL" />
              </Field>
            </FormGrid>
          </Card>

          {/* Proveedor */}
          <Card>
            <CardTitle>Proveedor <CardSubtitle>(Ordenado a)</CardSubtitle></CardTitle>
            <FormGrid>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Nombre / Razón social</Label>
                <Input value={form.provNombre} onChange={e => set('provNombre', e.target.value)} placeholder="Nombre del proveedor" />
              </Field>
              <Field>
                <Label>Contacto</Label>
                <Input value={form.provContacto} onChange={e => set('provContacto', e.target.value)} placeholder="Nombre del contacto" />
              </Field>
              <Field>
                <Label>Teléfono</Label>
                <Input value={form.provTel} onChange={e => set('provTel', e.target.value)} placeholder="Tel." />
              </Field>
              <Field>
                <Label>Fax</Label>
                <Input value={form.provFax} onChange={e => set('provFax', e.target.value)} placeholder="Fax" />
              </Field>
              <Field>
                <Label>RFC</Label>
                <Input value={form.provRfc} onChange={e => set('provRfc', e.target.value)} placeholder="RFC" />
              </Field>
              <Field>
                <Label>Dirección 1</Label>
                <Input value={form.provDir1} onChange={e => set('provDir1', e.target.value)} placeholder="Calle y número" />
              </Field>
              <Field>
                <Label>Dirección 2</Label>
                <Input value={form.provDir2} onChange={e => set('provDir2', e.target.value)} placeholder="Colonia" />
              </Field>
              <Field>
                <Label>Ciudad / Estado</Label>
                <Input value={form.provCiudad} onChange={e => set('provCiudad', e.target.value)} placeholder="Ciudad, Estado" />
              </Field>
              <Field>
                <Label>País</Label>
                <Input value={form.provPais} onChange={e => set('provPais', e.target.value)} placeholder="MEXICO" />
              </Field>
            </FormGrid>
          </Card>

          {/* Entrega y Comprador */}
          <Card>
            <CardTitle>Entrega y Comprador</CardTitle>
            <FormGrid>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Entregar a</Label>
                <Textarea value={form.entregarA} onChange={e => set('entregarA', e.target.value)}
                  placeholder="Dirección de entrega o nombre del almacén" rows={3} />
              </Field>
              <Field>
                <Label>No. Comprador</Label>
                <Input value={form.compradorNum} onChange={e => set('compradorNum', e.target.value)} placeholder="Ej: 54" />
              </Field>
              <Field>
                <Label>Nombre del comprador</Label>
                <Input value={form.compradorNombre} onChange={e => set('compradorNombre', e.target.value)} placeholder="Nombre completo" />
              </Field>
            </FormGrid>
          </Card>

          {/* Partidas */}
          <Card>
            <CardTitleRow>
              <CardTitle style={{ margin: 0 }}>Partidas</CardTitle>
              <IVAWrap>
                <Label>IVA&nbsp;(%)</Label>
                <IVAInput value={form.ivaPorc} onChange={e => set('ivaPorc', e.target.value)}
                  type="number" min="0" max="100" step="1" />
              </IVAWrap>
            </CardTitleRow>

            <ItemsScrollWrap>
              <ItemsTable>
                <thead>
                  <tr>
                    <ITh>Cantidad</ITh>
                    <ITh>Unidad</ITh>
                    <ITh>Atributo</ITh>
                    <ITh>Clave</ITh>
                    <ITh $wide>Producto</ITh>
                    <ITh>P. Unitario</ITh>
                    <ITh>P. Total</ITh>
                    <ITh>Fecha Entrega</ITh>
                    <ITh></ITh>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, i) => {
                    const lineTot = (parseFloat(item.cantidad)||0) * (parseFloat(item.precioUnit)||0);
                    return (
                      <tr key={i}>
                        <ITd><IInput value={item.cantidad} onChange={e => setItem(i,'cantidad',e.target.value)} placeholder="0" type="number" /></ITd>
                        <ITd><IInput value={item.unidad} onChange={e => setItem(i,'unidad',e.target.value)} placeholder="PIEZA" /></ITd>
                        <ITd><IInput value={item.atributo} onChange={e => setItem(i,'atributo',e.target.value)} /></ITd>
                        <ITd><IInput value={item.clave} onChange={e => setItem(i,'clave',e.target.value)} /></ITd>
                        <ITd $wide><IInput value={item.producto} onChange={e => setItem(i,'producto',e.target.value)} placeholder="Descripción" /></ITd>
                        <ITd><IInput value={item.precioUnit} onChange={e => setItem(i,'precioUnit',e.target.value)} placeholder="0.00" type="number" /></ITd>
                        <ITd $right $mono>{lineTot > 0 ? fmt(lineTot) : '—'}</ITd>
                        <ITd><IInput value={item.fechaEntrega} onChange={e => setItem(i,'fechaEntrega',e.target.value)} type="date" /></ITd>
                        <ITd>
                          {form.items.length > 1 && (
                            <DelBtn onClick={() => removeItem(i)} title="Eliminar partida"><FaTrash size={11}/></DelBtn>
                          )}
                        </ITd>
                      </tr>
                    );
                  })}
                </tbody>
              </ItemsTable>
            </ItemsScrollWrap>

            <AddItemBtn onClick={addItem}><FaPlus size={11} /> Agregar partida</AddItemBtn>

            <TotalesGrid>
              <TotRow><TotKey>Importe</TotKey><TotVal>${fmt(importe)}</TotVal></TotRow>
              <TotRow><TotKey>I.V.A. ({form.ivaPorc || 0}%)</TotKey><TotVal>${fmt(ivaAmt)}</TotVal></TotRow>
              <TotRow $total><TotKey>TOTAL</TotKey><TotVal>${fmt(total)}</TotVal></TotRow>
            </TotalesGrid>
          </Card>
        </FormWrap>

        {/* ── Ficha imprimible ── */}
        <PrintSheet>
          <DocWrap>

            {/* Cabecera del documento */}
            <DocHeader>
              <DocHeaderLeft>
                <DocLogo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
                <DocCompanyName>GRUPO JAYCOM</DocCompanyName>
                <DocCompanyLine>Fabricación de tarimas de madera</DocCompanyLine>
                <DocCompanyLine>contacto@grupojaycom.com</DocCompanyLine>
                {(form.provTel || form.provFax) && (
                  <DocTelFax>
                    {form.provTel && <span>Tel: {form.provTel}</span>}
                    {form.provFax && <span>Fax: {form.provFax}</span>}
                  </DocTelFax>
                )}
              </DocHeaderLeft>
              <DocHeaderRight>
                <DocTitle>Orden de Compra</DocTitle>
                <DocFolioTable>
                  <tbody>
                    <tr><DocInfoKey>Folio:</DocInfoKey><DocInfoVal $folio>{form.folio || '—'}</DocInfoVal></tr>
                    <tr><DocInfoKey>Fecha:</DocInfoKey><DocInfoVal>{fmtFecha(form.fecha)}</DocInfoVal></tr>
                    {form.tc && <tr><DocInfoKey>T.C.:</DocInfoKey><DocInfoVal>{form.tc}</DocInfoVal></tr>}
                    {form.sucursal && <tr><DocInfoKey>Sucursal:</DocInfoKey><DocInfoVal>{form.sucursal}</DocInfoVal></tr>}
                    <tr><DocInfoKey>Almacén:</DocInfoKey><DocInfoVal>{form.almacen || 'GENERAL'}</DocInfoVal></tr>
                  </tbody>
                </DocFolioTable>
              </DocHeaderRight>
            </DocHeader>

            {/* Proveedor / Entrega */}
            <VendorSection>
              <VendorCol>
                <SectionLabel>Ordenado a:</SectionLabel>
                {form.provNombre  && <VLine $bold>{form.provNombre}</VLine>}
                {form.provDir1    && <VLine>{form.provDir1}</VLine>}
                {form.provDir2    && <VLine>{form.provDir2}</VLine>}
                {form.provCiudad  && <VLine>{form.provCiudad}</VLine>}
                {(form.provRfc || form.provPais) && (
                  <VLineRow>
                    {form.provRfc  && <span>{form.provRfc}</span>}
                    {form.provPais && <span>{form.provPais}</span>}
                  </VLineRow>
                )}
              </VendorCol>
              <DeliveryCol>
                <SectionLabel>Entregar a:</SectionLabel>
                {form.entregarA && <VLine style={{ whiteSpace: 'pre-line' }}>{form.entregarA}</VLine>}
              </DeliveryCol>
            </VendorSection>

            {/* Contacto */}
            <ContactRow>
              <ContactLabel>Contacto:</ContactLabel>
              <span>{form.provContacto || '.'}</span>
            </ContactRow>

            {/* Comprador */}
            <BuyerRow>
              <BuyerBlock>
                <BuyerHeader>Proveedor:</BuyerHeader>
              </BuyerBlock>
              <BuyerBlock>
                <BuyerHeader>Comprador:</BuyerHeader>
                {(form.compradorNum || form.compradorNombre) && (
                  <BuyerInfo>
                    {form.compradorNum && <BuyerNum>{form.compradorNum}</BuyerNum>}
                    <span>{form.compradorNombre}</span>
                  </BuyerInfo>
                )}
              </BuyerBlock>
            </BuyerRow>

            {/* Tabla de partidas */}
            <DocTableWrap>
              <DocTable>
                <thead>
                  <tr>
                    <DcTh>Cantidad</DcTh>
                    <DcTh>Unidad</DcTh>
                    <DcTh>Atributo</DcTh>
                    <DcTh>Clave</DcTh>
                    <DcTh $wide>Producto</DcTh>
                    <DcTh $right>P. Unitario</DcTh>
                    <DcTh $right>P. Total</DcTh>
                    <DcTh>Fecha Entrega</DcTh>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, i) => {
                    const lineTot = (parseFloat(item.cantidad)||0) * (parseFloat(item.precioUnit)||0);
                    return (
                      <tr key={i}>
                        <DcTd>{item.cantidad || '—'}</DcTd>
                        <DcTd>{item.unidad || '—'}</DcTd>
                        <DcTd>{item.atributo || '—'}</DcTd>
                        <DcTd>{item.clave || '—'}</DcTd>
                        <DcTd $wide>{item.producto || '—'}</DcTd>
                        <DcTd $right>{item.precioUnit ? fmt(item.precioUnit) : '—'}</DcTd>
                        <DcTd $right>{lineTot > 0 ? fmt(lineTot) : '—'}</DcTd>
                        <DcTd>{item.fechaEntrega ? fmtFecha(item.fechaEntrega) : '—'}</DcTd>
                      </tr>
                    );
                  })}
                </tbody>
              </DocTable>
            </DocTableWrap>

            {/* Pie: importe en letras + totales */}
            <DocBottom>
              <DocWords>
                <DocWordsText>{numeroALetras(total)}</DocWordsText>
              </DocWords>
              <DocTotales>
                <DcTotRow><DcTotKey>Importe</DcTotKey><DcTotVal>{fmt(importe)}</DcTotVal></DcTotRow>
                <DcTotRow><DcTotKey>Subtotal</DcTotKey><DcTotVal>{fmt(importe)}</DcTotVal></DcTotRow>
                {ivaAmt > 0 && <DcTotRow><DcTotKey>I.V.A.</DcTotKey><DcTotVal>{fmt(ivaAmt)}</DcTotVal></DcTotRow>}
                <DcTotRow $total><DcTotKey>TOTAL</DcTotKey><DcTotVal>{fmt(total)}</DcTotVal></DcTotRow>
              </DocTotales>
            </DocBottom>

            <DocPageInfo>
              <span>Elaborado por Grupo Jaycom</span>
              <span>No. página / Páginas totales &nbsp; 1 / 1</span>
            </DocPageInfo>
          </DocWrap>
        </PrintSheet>

      </Page>
    </>
  );
}

/* ── Print CSS ── */
const PageStyle = createGlobalStyle`
  @page { size: A4 portrait; margin: 0; }
  @media print {
    .no-print { display: none !important; }
    body { background: white !important; margin: 0 !important;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  @media screen { .print-only { display: none !important; } }
`;

/* ── Layout ── */
const Page = styled.div`
  min-height: 100vh;
  background: var(--mid);
  padding: 2rem;
  font-family: 'Inter', sans-serif;
  @media (max-width: 600px) { padding: 1rem 0.75rem; }
  @media print { background: white; padding: 0; min-height: 0; }
`;

const Header = styled.header`
  max-width: 960px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  @media (max-width: 600px) { gap: 0.75rem; margin-bottom: 1rem; }
`;

const Logo = styled.img`height: 56px; object-fit: contain;`;

const HeaderTitle = styled.h1`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.7rem;
  color: var(--red);
  letter-spacing: 0.04em;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  @media (max-width: 600px) {
    width: 100%;
    & > button { flex: 1; justify-content: center; padding: 0.6rem 0.5rem; font-size: 0.75rem; }
  }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${({ $primary, $save }) => $primary ? 'var(--red)' : $save ? '#2e7d32' : 'var(--border)'};
  color: ${({ $primary, $save }) => ($primary || $save) ? '#fff' : 'var(--text)'};
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

/* ── Form ── */
const FormWrap = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  padding: 2rem 2rem 1.5rem;
  border-top: 3px solid var(--red);
  @media (max-width: 600px) { padding: 1.25rem 1rem 1rem; }
`;

const CardTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.2rem;
  color: var(--red);
  letter-spacing: 0.04em;
  margin-bottom: 1.25rem;
`;

const CardSubtitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: var(--text-sub);
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  margin-left: 0.4rem;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`display: flex; flex-direction: column; gap: 0.3rem;`;

const Label = styled.label`
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-sub);
`;

const Input = styled.input`
  border: none;
  border-bottom: 1.5px solid var(--border);
  background: transparent;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: var(--red); }
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;

const Textarea = styled.textarea`
  border: 1px solid var(--border);
  border-radius: 3px;
  background: transparent;
  padding: 0.5rem;
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  outline: none;
  resize: vertical;
  transition: border-color 0.2s;
  &:focus { border-color: var(--red); }
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;

const IVAWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const IVAInput = styled.input`
  width: 64px;
  border: none;
  border-bottom: 1.5px solid var(--border);
  background: transparent;
  padding: 0.4rem 0;
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  outline: none;
  text-align: center;
  &:focus { border-color: var(--red); }
`;

/* ── Items table (form) ── */
const ItemsScrollWrap = styled.div`overflow-x: auto; margin-bottom: 0.75rem;`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  min-width: 800px;
`;

const ITh = styled.th`
  background: var(--mid);
  padding: 0.45rem 0.5rem;
  text-align: left;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-sub);
  white-space: nowrap;
  border-bottom: 1px solid var(--border);
  ${p => p.$wide && 'width: 30%;'}
`;

const ITd = styled.td`
  padding: 0.3rem 0.35rem;
  border-bottom: 1px solid var(--border);
  text-align: ${p => p.$right ? 'right' : 'left'};
  font-family: ${p => p.$mono ? 'monospace' : 'inherit'};
  ${p => p.$wide && 'min-width: 180px;'}
  white-space: nowrap;
`;

const IInput = styled.input`
  width: 100%;
  border: none;
  border-bottom: 1px dashed var(--border);
  background: transparent;
  padding: 0.25rem 0;
  font-size: 0.82rem;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  outline: none;
  min-width: 60px;
  &:focus { border-color: var(--red); }
  &[type="date"] { min-width: 120px; }
`;

const DelBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--red);
  padding: 0.2rem 0.35rem;
  opacity: 0.5;
  &:hover { opacity: 1; }
`;

const AddItemBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: 1.5px dashed var(--border);
  border-radius: 3px;
  color: var(--red);
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.4rem 0.85rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: var(--red); background: var(--red-lt); }
`;

const TotalesGrid = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-end;
`;

const TotRow = styled.div`
  display: flex;
  gap: 1.5rem;
  font-size: ${p => p.$total ? '1rem' : '0.85rem'};
  font-weight: ${p => p.$total ? '700' : '400'};
  color: ${p => p.$total ? 'var(--red)' : 'var(--text)'};
  padding-top: ${p => p.$total ? '0.5rem' : '0'};
  border-top: ${p => p.$total ? '2px solid var(--red)' : 'none'};
  font-family: ${p => p.$total ? "'Bebas Neue', sans-serif" : 'inherit'};
  letter-spacing: ${p => p.$total ? '0.04em' : '0'};
`;

const TotKey = styled.span`min-width: 140px; text-align: right; color: var(--text-sub);`;
const TotVal = styled.span`font-family: monospace; min-width: 90px; text-align: right;`;

/* ── Print Sheet ── */
const PrintSheet = styled.div`
  max-width: 960px;
  margin: 2rem auto 0;
  @media print { margin: 0; max-width: 100%; }
`;

const DocWrap = styled.div`
  background: white;
  border: 1px solid #ccc;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.78rem;
  color: #111;
  @media print {
    border: none;
    padding: 1cm 1.5cm 1.5cm;
    font-size: 8pt;
  }
  @media screen {
    padding: 1.5rem;
    border-top: 4px solid var(--red);
  }
`;

const DocHeader = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #999;
`;

const DocHeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const DocLogo = styled.img`
  height: 50px;
  object-fit: contain;
  object-position: left;
  margin-bottom: 0.25rem;
`;

const DocCompanyName = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #1F5D8C;
  letter-spacing: 0.05em;
`;

const DocCompanyLine = styled.div`
  font-size: 0.7rem;
  color: #555;
`;

const DocTelFax = styled.div`
  display: flex;
  gap: 1.5rem;
  font-size: 0.7rem;
  color: #333;
  margin-top: 0.25rem;
`;

const DocHeaderRight = styled.div`
  min-width: 200px;
  text-align: right;
`;

const DocTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  color: #111;
  font-family: Arial, Helvetica, sans-serif;
  margin-bottom: 0.4rem;
`;

const DocFolioTable = styled.table`
  margin-left: auto;
  border-collapse: collapse;
  font-size: 0.72rem;
`;

const DocInfoKey = styled.td`
  padding: 1px 0.5rem 1px 0;
  color: #555;
  text-align: right;
  white-space: nowrap;
`;

const DocInfoVal = styled.td`
  padding: 1px 0;
  font-weight: ${p => p.$folio ? '900' : '600'};
  font-size: ${p => p.$folio ? '1.1rem' : 'inherit'};
  text-align: right;
  white-space: nowrap;
`;

/* ── Vendor / Delivery ── */
const VendorSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #bbb;
  border-top: none;
`;

const VendorCol = styled.div`
  padding: 0.5rem 0.75rem;
  border-right: 1px solid #bbb;
`;

const DeliveryCol = styled.div`
  padding: 0.5rem 0.75rem;
`;

const SectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 0.3rem;
`;

const VLine = styled.div`
  font-size: 0.78rem;
  font-weight: ${p => p.$bold ? '700' : '400'};
  line-height: 1.45;
`;

const VLineRow = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.78rem;
  margin-top: 0.15rem;
`;

/* ── Contact / Buyer rows ── */
const ContactRow = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid #bbb;
  border-top: none;
  font-size: 0.78rem;
`;

const ContactLabel = styled.span`font-weight: 700;`;

const BuyerRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #bbb;
  border-top: none;
  margin-bottom: 0;
`;

const BuyerBlock = styled.div`
  padding: 0.35rem 0.75rem;
  &:first-child { border-right: 1px solid #bbb; }
`;

const BuyerHeader = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
  margin-bottom: 0.2rem;
`;

const BuyerInfo = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.78rem;
`;

const BuyerNum = styled.span`font-weight: 700; color: #1F5D8C;`;

/* ── Items table (print) ── */
const DocTableWrap = styled.div`overflow-x: auto;`;

const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  border: 1px solid #bbb;
  border-top: none;
`;

const DcTh = styled.th`
  background: #1F5D8C;
  color: white;
  padding: 0.4rem 0.5rem;
  text-align: ${p => p.$right ? 'right' : 'left'};
  font-size: 0.65rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  ${p => p.$wide && 'width: 25%;'}
`;

const DcTd = styled.td`
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid #e5e5e5;
  text-align: ${p => p.$right ? 'right' : 'left'};
  ${p => p.$wide && 'min-width: 120px;'}
`;

/* ── Footer: totals + words ── */
const DocBottom = styled.div`
  display: flex;
  border: 1px solid #bbb;
  border-top: none;
`;

const DocWords = styled.div`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border-right: 1px solid #bbb;
  display: flex;
  align-items: center;
`;

const DocWordsText = styled.em`
  font-size: 0.72rem;
  font-style: italic;
  color: #333;
  line-height: 1.4;
`;

const DocTotales = styled.div`
  min-width: 200px;
  padding: 0.35rem 0.75rem;
`;

const DcTotRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 2px 0;
  font-size: ${p => p.$total ? '0.85rem' : '0.72rem'};
  font-weight: ${p => p.$total ? '700' : '400'};
  border-top: ${p => p.$total ? '1px solid #333' : 'none'};
  margin-top: ${p => p.$total ? '3px' : '0'};
`;

const DcTotKey = styled.span`color: #555; white-space: nowrap;`;
const DcTotVal = styled.span`font-family: monospace; text-align: right;`;

const DocPageInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0.75rem;
  border: 1px solid #bbb;
  border-top: none;
  font-size: 0.65rem;
  color: #888;
`;
